import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { propertyNameQueryForFastApi } from './cashReconPropertyApi';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateFormFieldsSx,
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';

const Compare = () => {
  const navigate = useNavigate();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  
  const [mappings, setMappings] = useState([]);
  const [comparisonRows, setComparisonRows] = useState([]);
  const [groupedRows, setGroupedRows] = useState([]); // For table with rowspan
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  
  // Filter states
  const [selectedProperty, setSelectedProperty] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  const [balanceData, setBalanceData] = useState({
    glBalance: 0,
    bankBalance: 0,
    delta: 0,
    endingBankBalance: 0,
    glDebits: 0,
    glCredits: 0,
    bankDebits: 0,
    bankCredits: 0,
    totalMappings: 0,
  });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceFetched, setBalanceFetched] = useState(false);

  const monthYearFromISODate = (dateStr) => {
    if (!dateStr || dateStr.length < 7) return '';
    return dateStr.slice(0, 7);
  };

  const fetchPropertyOperatingAccount = async (hotelSelection) => {
    const apiPropertyName = propertyNameQueryForFastApi(hotelSelection, properties);
    if (!apiPropertyName) return null;
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');
    if (!token || !tenantId) return null;
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/property-account-list?tenant_id=${encodeURIComponent(tenantId)}&property_name=${encodeURIComponent(apiPropertyName)}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      if (json.status === 'success' && Array.isArray(json.data) && json.data.length > 0) {
        const first = json.data[0];
        return first.OPERATING_ACCOUNT != null ? String(first.OPERATING_ACCOUNT).trim() : null;
      }
      return null;
    } catch (err) {
      console.error('Error fetching property operating account:', err);
      return null;
    }
  };

  const fetchBankAccountForCompare = async (propertyId, startDateStr, propertyName) => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_id');
    if (!token || !tenantId || !propertyId) return null;
    try {
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/by-property?tenant_id=${tenantId}&property_id=${encodeURIComponent(propertyId)}`;
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data) && data.length > 0) {
          const my = monthYearFromISODate(startDateStr);
          const match = my ? data.find((h) => h.monthYear === my) : null;
          const raw = match?.bankAccountNo ?? data[0]?.bankAccountNo;
          if (raw != null && String(raw).trim() !== '') {
            return String(raw).trim();
          }
        }
      }
    } catch (err) {
      console.error('Error fetching mapping headers for bank account:', err);
    }
    if (propertyName && propertyName.trim()) {
      return fetchPropertyOperatingAccount(propertyName);
    }
    return null;
  };

  const applyChaseEndingBalance = async (bankAccountNo, fromDate, toDate) => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');
    if (!token || !tenantId || !bankAccountNo || !fromDate || !toDate) return;
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const apiUrl = `${baseUrl}fastapi/v1/cash-recon/bank-data-chase-summary?tenant_id=${encodeURIComponent(tenantId)}&start_date=${encodeURIComponent(fromDate)}&end_date=${encodeURIComponent(toDate)}&customer_account_number=${encodeURIComponent(bankAccountNo)}`;
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.status !== 'success' || !Array.isArray(data.data) || data.data.length === 0) return;
      const latestRecord = data.data.reduce((latest, current) => {
        if (!latest) return current;
        return new Date(current.AS_OF_DATE) > new Date(latest.AS_OF_DATE) ? current : latest;
      });
      const closingAvailable = latestRecord.CLOSING_AVAILABLE || 0;
      setBalanceData((prev) => ({
        ...prev,
        endingBankBalance: closingAvailable,
      }));
    } catch (err) {
      console.error('Error fetching bank data chase summary:', err);
    }
  };

  const fetchBalanceData = async (propertyId, fromDate, toDate, propertyName) => {
    if (!propertyId || !fromDate || !toDate) {
      setBalanceFetched(false);
      return;
    }
    try {
      setBalanceLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        return;
      }

      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/balance?tenant_id=${tenantId}`;
      apiUrl += `&property_id=${propertyId}`;
      apiUrl += `&from_date=${fromDate}`;
      apiUrl += `&to_date=${toDate}`;

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data.status === 'success') {
          const bankBal = data.bankBalance || 0;
          setBalanceData({
            glBalance: data.glBalance || 0,
            bankBalance: bankBal,
            delta: data.delta || 0,
            endingBankBalance: data.endingBankBalance ?? bankBal,
            glDebits: data.glDebits || 0,
            glCredits: data.glCredits || 0,
            bankDebits: data.bankDebits || 0,
            bankCredits: data.bankCredits || 0,
            totalMappings: data.totalMappings || 0,
          });
          setBalanceFetched(true);

          const bankNo = await fetchBankAccountForCompare(propertyId, fromDate, propertyName || '');
          if (bankNo) {
            await applyChaseEndingBalance(bankNo, fromDate, toDate);
          }
          return;
        }
      }
      setBalanceData({
        glBalance: 0,
        bankBalance: 0,
        delta: 0,
        endingBankBalance: 0,
        glDebits: 0,
        glCredits: 0,
        bankDebits: 0,
        bankCredits: 0,
        totalMappings: 0,
      });
      setBalanceFetched(false);
    } catch (err) {
      console.error('Error fetching balance data:', err);
      setBalanceFetched(false);
    } finally {
      setBalanceLoading(false);
    }
  };

  // Fetch properties list
  const fetchProperties = async () => {
    try {
      setPropertiesLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        return;
      }

      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const apiUrl = `${baseUrl}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;
      
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data && Array.isArray(data)) {
          const processedData = data.map((item, index) => ({
            id: item.propertyId || index + 1,
            propertyId: item.propertyId || '',
            propertyName: item.propertyName || '',
            propertyCode: item.propertyCode || '',
            propertyGroupId: item.propertyGroupId || '',
            companyId: item.companyId || '',
            addressLine1: item.addressLine1 || '',
            city: item.city || '',
            state: item.state || '',
            country: item.country || '',
            postalCode: item.postalCode || '',
            baseCurrency: item.baseCurrency || 'USD',
            defaultBankAccountId: item.defaultBankAccountId || null,
            cashReconEnabled: item.cashReconEnabled !== false,
            status: item.status || 'ACTIVE',
            deleted: item.deleted || false,
            createdAt: item.createdAt || '',
            updatedAt: item.updatedAt || '',
          }));
          setProperties(processedData);
        }
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    } finally {
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  // Fetch mappings from API
  const fetchMappings = async () => {
    if (!selectedProperty || selectedProperty === '' || !startDate || !endDate) {
      showError('Please select Property, Start Date, and End Date');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setBalanceFetched(false);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      // Find property ID from selected property name
      const selectedPropertyData = properties.find(p => p.propertyName === selectedProperty);
      const propertyId = selectedPropertyData ? selectedPropertyData.propertyId : '';

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const apiUrl = `${baseUrl}conductor/cash-recon/recon-mapping-details/matched-records?tenant_id=${tenantId}&property_id=${propertyId}&start_date=${startDate}&end_date=${endDate}`;
      
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch mappings: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();
      
      if (data && Array.isArray(data)) {
        // Process the matched records data directly
        const processedData = data.map(item => ({
          id: item.id,
          bankId: item.bankStatementId,
          bankDescription: item.bankDescription || '',
          bankDate: item.bankDate || '',
          bankDebit: item.bankDebit || 0,
          bankCredit: item.bankCredit || 0,
          glDescription: item.glDescription || '',
          glDate: item.glDate || '',
          glDebit: item.glDebit || 0,
          glCredit: item.glCredit || 0,
          propertyId: item.propertyId || '',
        }));

        // Group by bank statement ID for table display
        const groupedData = new Map();
        processedData.forEach(item => {
          if (!groupedData.has(item.bankId)) {
            groupedData.set(item.bankId, {
              bankId: item.bankId,
              bankDescription: item.bankDescription,
              bankDate: item.bankDate,
              bankDebit: item.bankDebit,
              bankCredit: item.bankCredit,
              glRows: []
            });
          }
          groupedData.get(item.bankId).glRows.push({
            id: item.id,
            glDescription: item.glDescription,
            glDate: item.glDate,
            glDebit: item.glDebit,
            glCredit: item.glCredit
          });
        });

        setGroupedRows(Array.from(groupedData.values()));
        
        // Also create flat structure for pagination
        const flatRows = [];
        Array.from(groupedData.values()).forEach(group => {
          group.glRows.forEach((glRow, idx) => {
            flatRows.push({
              id: `${group.bankId}_${glRow.id}_${idx}`,
              bankId: group.bankId,
              bankDescription: idx === 0 ? group.bankDescription : '',
              bankDate: idx === 0 ? group.bankDate : '',
              bankDebit: idx === 0 ? group.bankDebit : null,
              bankCredit: idx === 0 ? group.bankCredit : null,
              isFirstInGroup: idx === 0,
              glDescription: glRow.glDescription,
              glDate: glRow.glDate,
              glDebit: glRow.glDebit,
              glCredit: glRow.glCredit,
            });
          });
        });
        setComparisonRows(flatRows);

        if (propertyId && startDate && endDate) {
          await fetchBalanceData(propertyId, startDate, endDate, selectedProperty);
        }
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      setError(err.message);
      setGroupedRows([]);
      setComparisonRows([]);
      setBalanceFetched(false);
      setBalanceData({
        glBalance: 0,
        bankBalance: 0,
        delta: 0,
        endingBankBalance: 0,
        glDebits: 0,
        glCredits: 0,
        bankDebits: 0,
        bankCredits: 0,
        totalMappings: 0,
      });
      showError(err.message || 'Failed to fetch mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    if (!groupedRows.length) {
      showError('No data to export. Select filters and click Load first.');
      return;
    }

    const exportRows = [];
    groupedRows.forEach((group) => {
      group.glRows.forEach((glRow) => {
        exportRows.push({
          'Bank Description': group.bankDescription || '',
          'Bank Date': group.bankDate || '',
          'Bank Debit': Number(group.bankDebit) || 0,
          'Bank Credit': Number(group.bankCredit) || 0,
          'GL Description': glRow.glDescription || '',
          'GL Date': glRow.glDate || '',
          'GL Debit': Number(glRow.glDebit) || 0,
          'GL Credit': Number(glRow.glCredit) || 0,
        });
      });
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet['!cols'] = [
      { wch: 36 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 36 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Compare');

    const safeName = (selectedProperty || 'export').replace(/[^\w\-]+/g, '_').slice(0, 40);
    const filename = `cash-recon-compare_${safeName}_${startDate}_${endDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
    showSuccess(`Successfully exported ${exportRows.length} row(s) to ${filename}`, 'Export Complete');
  };

  // Process mappings and group by cash_recon_id
  const processMappings = async (mappingList) => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration');
      
      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      // Get unique cash_recon_ids and cash_recon_ref_ids from mappings
      const uniqueBankIds = new Set(mappingList.map(m => m.cash_recon_id).filter(Boolean));
      const uniqueGlIds = new Set(mappingList.map(m => m.cash_recon_ref_id).filter(Boolean));

      // Fetch Bank records with same filters
      const bankRecordsMap = new Map();
      let bankUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/bank-normalized-data-view?tenant_id=${tenantId}&bank_transaction_month=${new Date(startDate).getMonth() + 1}&bank_transaction_year=${new Date(startDate).getFullYear()}&limit=1000&offset=0`;
      
      // Add property_name only if not "All"
      if (selectedProperty && selectedProperty !== 'All') {
        const encodedProperty = encodeURIComponent(selectedProperty);
        bankUrl += `&property_name=${encodedProperty}`;
      }
      const bankResp = await fetch(bankUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (bankResp.ok) {
        const bankData = await bankResp.json();
        if (bankData.status === 'success' && Array.isArray(bankData.data)) {
          bankData.data.forEach(record => {
            // Only include records that are in our mapping list
            if (uniqueBankIds.has(record.CASH_RECON_ID)) {
              bankRecordsMap.set(record.CASH_RECON_ID, record);
            }
          });
        }
      }

      // Fetch GL records with same filters
      const glRecordsMap = new Map();
      let glUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/bank-reference-data-view?tenant_id=${tenantId}&start_date=${startDate}&end_date=${endDate}&limit=1000&offset=0`;
      
      // Add property_name only if not "All"
      if (selectedProperty && selectedProperty !== 'All') {
        const encodedProperty = encodeURIComponent(selectedProperty);
        glUrl += `&property_name=${encodedProperty}`;
      }
      const glResp = await fetch(glUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (glResp.ok) {
        const glData = await glResp.json();
        if (glData.status === 'success' && Array.isArray(glData.data)) {
          glData.data.forEach(record => {
            // Only include records that are in our mapping list
            if (uniqueGlIds.has(record.CASH_RECON_REF_ID)) {
              glRecordsMap.set(record.CASH_RECON_REF_ID, record);
            }
          });
        }
      }

      // Group mappings by cash_recon_id
      const groupedMappings = new Map();
      mappingList.forEach(mapping => {
        const bankId = mapping.cash_recon_id;
        if (!groupedMappings.has(bankId)) {
          groupedMappings.set(bankId, []);
        }
        groupedMappings.get(bankId).push(mapping);
      });

      // Build grouped rows for table with rowspan
      const groupedRowsData = [];

      groupedMappings.forEach((mappingGroup, bankId) => {
        const bankRecord = bankRecordsMap.get(bankId);
        const glRows = [];
        
        // Create GL rows for this bank group
        mappingGroup.forEach((mapping) => {
          const glRecord = glRecordsMap.get(mapping.cash_recon_ref_id);
          glRows.push({
            id: mapping.id,
            glDescription: glRecord?.GL_DESCRIPTION || glRecord?.GL_DESCRIPTION_MEMO || '',
            glDate: glRecord?.GL_POSTING_DATE || '',
            glDebit: glRecord?.GL_DEBITS || 0,
            glCredit: glRecord?.GL_CREDITS || 0,
          });
        });

        // Create grouped row with bank data and GL rows
        groupedRowsData.push({
          bankId: bankId,
          bankDescription: bankRecord?.BANK_DESCRIPTION || '',
          bankDate: bankRecord?.BANK_TRANSACTION_DATE || '',
          bankDebit: bankRecord?.BANK_DEBITS || 0,
          bankCredit: bankRecord?.BANK_CREDITS || 0,
          glRows: glRows,
          rowspan: glRows.length, // Number of GL rows (for rowspan)
        });
      });

      setGroupedRows(groupedRowsData);
      
      // Also keep flat structure for backward compatibility if needed
      const combinedRows = [];
      groupedRowsData.forEach(group => {
        group.glRows.forEach((glRow, idx) => {
          combinedRows.push({
            id: `${group.bankId}_${glRow.id}_${idx}`,
            bankId: group.bankId,
            bankDescription: idx === 0 ? group.bankDescription : '',
            bankDate: idx === 0 ? group.bankDate : '',
            bankDebit: idx === 0 ? group.bankDebit : null,
            bankCredit: idx === 0 ? group.bankCredit : null,
            isFirstInGroup: idx === 0,
            glDescription: glRow.glDescription,
            glDate: glRow.glDate,
            glDebit: glRow.glDebit,
            glCredit: glRow.glCredit,
          });
        });
      });
      setComparisonRows(combinedRows);
    } catch (err) {
      console.error('Error processing mappings:', err);
      showError('Failed to process mapped records');
    }
  };

  const columns = [
    // Bank columns
    { 
      field: 'bankDescription', 
      headerName: 'Bank Description', 
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.bankDescription || '',
    },
    {
      field: 'bankDate', 
      headerName: 'Bank Date', 
      width: 120,
      valueGetter: (params) => params.row.bankDate || '',
    },
    {
      field: 'bankDebit', 
      headerName: 'Bank Debit', 
      width: 120,
      type: 'number',
      valueGetter: (params) => {
        // Return null for merged rows (not first in group)
        if (params.row.bankDebit === null || params.row.bankDebit === undefined) {
          return null;
        }
        return params.row.bankDebit || 0;
      },
      valueFormatter: (params) => {
        // Show empty string for merged rows
        if (params.value === null || params.value === undefined) {
          return '';
        }
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'bankCredit', 
      headerName: 'Bank Credit', 
      width: 120,
      type: 'number',
      valueGetter: (params) => {
        // Return null for merged rows (not first in group)
        if (params.row.bankCredit === null || params.row.bankCredit === undefined) {
          return null;
        }
        return params.row.bankCredit || 0;
      },
      valueFormatter: (params) => {
        // Show empty string for merged rows
        if (params.value === null || params.value === undefined) {
          return '';
        }
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    // GL columns
    {
      field: 'glDescription', 
      headerName: 'GL Description', 
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.glDescription || '',
    },
    {
      field: 'glDate', 
      headerName: 'GL Date', 
      width: 120,
      valueGetter: (params) => params.row.glDate || '',
    },
    {
      field: 'glDebit', 
      headerName: 'GL Debit', 
      width: 120,
      type: 'number',
      valueGetter: (params) => params.row.glDebit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'glCredit', 
      headerName: 'GL Credit', 
      width: 120,
      type: 'number',
      valueGetter: (params) => params.row.glCredit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
  ];

  return (
    <Box
      sx={{
        ...harmonatePageSx,
        height: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={harmonatePlainIconButtonSx}
            aria-label="Back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={harmonatePageTitleSx}>
            Cash Reconciliation - Compare
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{ ...harmonateCardSx, ...harmonateFormFieldsSx, p: 2, mb: 2, flexShrink: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
            <Autocomplete
              disabled={propertiesLoading}
              options={properties}
              getOptionLabel={(option) => {
                const code = option.propertyCode || '';
                const name = option.propertyName || '';
                return code && name ? `${code} - ${name}` : name;
              }}
              value={properties.find(p => p.propertyName === selectedProperty) || null}
              onChange={(event, newValue) => {
                setSelectedProperty(newValue ? newValue.propertyName : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Property / Hotel"
                  size="small"
                  placeholder="Select Property"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {propertiesLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.endAdornment
                        )}
                      </>
                    )
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.propertyName === value?.propertyName}
              sx={{ fontSize: '14px' }}
            />
            <TextField
              size="small"
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={fetchMappings}
              disabled={loading || !selectedProperty || !startDate || !endDate}
              sx={harmonateProceedButtonSx}
            >
              {loading ? 'Loading...' : 'Load'}
            </Button>
          </Box>

          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0,
              py: { xs: 0.5, sm: 0 },
            }}
          >
            {balanceFetched && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    Variance
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      color: balanceData.delta < 0 ? ht.red : ht.green,
                    }}
                  >
                    {balanceLoading ? (
                      <CircularProgress size={16} sx={{ color: ht.green }} />
                    ) : (
                      new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        signDisplay: 'always',
                      }).format(balanceData.delta)
                    )}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    Ending Bank Account Balance
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000', lineHeight: 1.2 }}>
                    {balanceLoading ? (
                      <CircularProgress size={16} sx={{ color: ht.green }} />
                    ) : (
                      new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                      }).format(balanceData.endingBankBalance)
                    )}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={loading || !groupedRows.length}
              sx={harmonateUploadOutlinedSx}
            >
              Export to Excel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Comparison Grid - same DataGrid as Output */}
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            minHeight: 0,
          }}
        >
          <CircularProgress sx={{ color: ht.green }} />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <DataGrid
            rows={comparisonRows}
            columns={columns}
            rowHeight={40}
            loading={false}
            pagination
            paginationMode="client"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            sx={{ ...harmonateDataGridSx, flex: 1, minHeight: 0 }}
            disableSelectionOnClick
            getRowId={(row) => row.id}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    Select filters and click Load to view comparison data
                  </Typography>
                </Box>
              ),
            }}
          />
        </Paper>
      )}

      {/* Toast Notification */}
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default Compare;
